/**
 * Per-area bet bag for the 9 BandarQQ positions (291-299).
 * Mirrors packages/amgbandarqq_v2/lib/domain/entities/bet_collection.dart
 * — bets are keyed by `betItemId` (no enum, since there's nine of them).
 */
import type { BetItemId } from '../types/game';
import { isBetItemId } from '../types/game';

export interface BetEntry {
  betItemId: BetItemId;
  amount: number;
  /** History of chip denominations placed (for animations/undo). */
  chips: number[];
}

export class BetCollection {
  private entries: Map<BetItemId, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { betItemId: v.betItemId, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(betItemId: BetItemId, amount: number): void {
    const cur = this.entries.get(betItemId);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(betItemId, { betItemId, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(betItemId: BetItemId): void {
    this.entries.delete(betItemId);
  }

  amountFor(betItemId: BetItemId): number {
    return this.entries.get(betItemId)?.amount ?? 0;
  }

  chipsFor(betItemId: BetItemId): readonly number[] {
    return this.entries.get(betItemId)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get betIds(): readonly BetItemId[] {
    return Array.from(this.entries.keys());
  }

  /** Convert into the `betDetail` lines expected by /action/bet. */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: v.betItemId, amount: v.amount });
    }
    return out;
  }

  /**
   * Like toBetLines() but emits one line per INDIVIDUAL chip placed, instead
   * of one line carrying the per-area total.
   *
   * Used for the multiplayer SignalR hub (`hub.placeBet`), which validates
   * every submitted `amount` against the configured chip denominations and
   * rejects a summed total (e.g. 100+100+100=300) with "Invalid Chip Amount".
   * Submitting one placeBet per chip keeps each amount a valid denomination.
   *
   * Invariant: for each betItemId, the sum of toChipLines() amounts equals the
   * toBetLines() amount (same betItemId derivation, same `<= 0` skip).
   */
  toChipLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      for (const chip of v.chips) {
        if (chip <= 0) continue;
        out.push({ betItemId: v.betItemId, amount: chip });
      }
    }
    return out;
  }

  /** Lookup with runtime guard for foreign IDs. */
  static normaliseId(n: number): BetItemId | null {
    return isBetItemId(n) ? n : null;
  }
}
